import * as Yup from 'yup';
import { get, omit, pick } from 'lodash';

import i18n from 'i18n';
import { isQuestionMultiType } from 'services/questions/helpers';
import { ALLOWED_FIELDS_TO_ANSWER } from 'components/questions/Question/constants';

import { QuestionType } from 'types/enums';
import { CreateQuestionValues } from './interfaces';

export const createQuestionValidationSchema = Yup.object().shape({
  question: Yup.object().shape({
    title: Yup.string()
      .trim()
      .when('$', {
        is: ({ filePreview, question }) => {
          if (
            !!filePreview ||
            !!question.youtubeLink?.link ||
            !!question.loomLink?.link ||
            !!question.vimeoLink?.link ||
            !!question.tenorLink?.link ||
            !!question.files.length ||
            !!question.button.link ||
            !!question.images.length
          )
            return false;

          return true;
        },
        then: Yup.string()
          .required(i18n.t('required'))
          .min(1),
        otherwise: (schema: any) => schema.notRequired().nullable(),
      }),
    options: Yup.array().when('$settings', (settings, schema) =>
      isQuestionMultiType(settings.type)
        ? Yup.array().of(
            Yup.object().shape({
              title: Yup.string()
                .nullable()
                .when(['isOther'], {
                  is: isOther => !isOther,
                  then: Yup.string()
                    .trim()
                    .required(i18n.t('required')),
                }),
              images: Yup.array().when('$settings', (settings, schema) =>
                settings.optionImagesEnabled
                  ? schema.test(
                      i18n.t('required'),
                      (images: QuestionImage[]) => !!images?.filter(Boolean).length
                    )
                  : schema
              ),
            })
          )
        : schema
    ),
  }),
});

export const DEFAULT_QUESTION_OPTIONS = [
  { title: 'Option 1', images: [] },
  { title: 'Option 2', images: [] },
];

export const NEW_OPTION_PLACEHOLDER = { title: '', images: [] };

export const DEFAULT_QUESTION_DATA = {
  question: {
    title: '',
    images: [],
    files: [],
    options: DEFAULT_QUESTION_OPTIONS,
    youtubeLink: {
      link: null,
    },
    loomLink: {
      link: null,
    },
    vimeoLink: {
      link: null,
    },
    tenorLink: {
      link: null,
    },
    button: {
      text: null,
      link: null,
    },
  },
  settings: {
    type: QuestionType.header,
    hasOther: false,
    optionImagesEnabled: false,
    notifyMe: false,
  },
};

export const ALLOWED_FIELDS_TO_CREATE = ['question', 'settings'];

export const ALLOWED_FIELDS_TO_UPDATE = [...ALLOWED_FIELDS_TO_CREATE, 'selected', 'imagesToRemove'];

export const MIN_OPTIONS_COUNT = 2;

export const MAX_OPTIONS_WITH_IMAGES_COUNT = 15;

export const sortOtherOption = (op1: QuestionOption, op2: QuestionOption) => {
  if (op1.isOther && !op2.isOther) {
    return 1;
  } else if (!op1.isOther && op2.isOther) {
    return -1;
  } else {
    return 0;
  }
};

export const FIELDS_TO_TRACK_CLEAR_ANSWER = [
  'question.options',
  'settings.type',
  'settings.hasOther',
  'settings.optionImagesEnabled',
];

export const getPreparedQuestionPayload = (values: CreateQuestionValues, isEditMode: boolean) => {
  const type = values.settings.type;
  const withOptions = type === QuestionType.checkbox || type === QuestionType.radio;

  return {
    ...pick(values, ALLOWED_FIELDS_TO_CREATE),
    settings: withOptions
      ? values.settings
      : {
          ...values.settings,
          optionImagesEnabled: false,
          hasOther: false,
        },
    question: {
      ...omit(
        values.question,
        isEditMode ? ['updated'] : ['imagesToRemove', 'filesToRemove', 'updated']
      ),
      files: values.question.files?.filter(option => !option.fileUrl),
      images: values.question.images?.filter(option => !option.imageUrl),
      options: withOptions
        ? values.question.options
            .map(
              option =>
                !!option.title &&
                !option.isOther && {
                  title: option.title,
                  images: values.settings.optionImagesEnabled ? option.images?.filter(Boolean) : [],
                }
            )
            .filter(Boolean)
        : [],
      youtubeLink: {
        link: values.question.youtubeLink?.link,
      },
      loomLink: {
        link: values.question.loomLink?.link,
      },
      vimeoLink: {
        link: values.question.vimeoLink?.link,
      },
    },
  };
};

export const getPreparedAnswerPayload = (values: UpdateQuestionForm) => {
  const value = pick(values, ALLOWED_FIELDS_TO_ANSWER);

  return {
    answer: {
      ...value.answer,
      contact: {
        ...value.answer?.contact,
        resources: value.answer?.contact.resources ?? [],
      },
    },
  };
};

export const normalizeOptions = (options: QuestionOption[]) =>
  options
    .filter(o => !o.isOther)
    .map(option => ({
      title: option.title,
      images: option.images,
    }));

export const shouldDropAnswer = (oldPayload: Question, newPayload: Question) => {
  return FIELDS_TO_TRACK_CLEAR_ANSWER.some(field => {
    let oldVal = get(oldPayload, field);
    let newVal = get(newPayload, field);

    if (field === 'question.options') {
      oldVal = normalizeOptions(oldVal);
      newVal = normalizeOptions(newVal);
    }

    return JSON.stringify(oldVal) !== JSON.stringify(newVal);
  });
};
